import { useNavigate } from "react-router-dom";
import { ArrowLeft, Shield, Flag, Ban, Trash2 } from "lucide-react";
import LanguageToggle from "../components/LanguageToggle";
import { motion } from "framer-motion";

const RULES = {
  fr: [
    { icon: "✦", title: "Reste dans le cadre du skill", desc: "Chaque échange tourne autour d'une compétence, d'une aide ou d'une activité concrète. Reste clair et utile." },
    { icon: "◎", title: "Sois respectueux·se", desc: "Pas de harcèlement, pas de spam, pas de discrimination. Chaque personne mérite une interaction de qualité." },
    { icon: "⌖", title: "Privilégie les lieux publics", desc: "Campus, café, bibliothèque, visio. Pas de contrainte, mais c'est la bonne pratique pour un premier échange." },
    { icon: "✉", title: "Un message structuré", desc: "Ton premier message décrit le skill, l'objectif et le format. Pas de message flou, pas de relance avant réponse." },
    { icon: "◉", title: "Données protégées", desc: "Pas de téléphone public. Zones approximatives uniquement. Aucune géolocalisation en direct." },
    { icon: "⚑", title: "Signale ce qui est hors cadre", desc: "Comportement inapproprié ? Bloquer et signaler. L'équipe modère activement." },
  ],
  en: [
    { icon: "✦", title: "Stay in the skill frame", desc: "Every exchange centers on a skill, help or activity. Be clear and useful." },
    { icon: "◎", title: "Be respectful", desc: "No harassment, no spam, no discrimination. Every person deserves quality interaction." },
    { icon: "⌖", title: "Prefer public places", desc: "Campus, café, library, video call. No obligation, but best practice for a first exchange." },
    { icon: "✉", title: "One structured message", desc: "Your first message describes the skill, goal and format. No vague messages, no follow-up before a reply." },
    { icon: "◉", title: "Data protected", desc: "No public phone number. Approximate zones only. No live geolocation." },
    { icon: "⚑", title: "Report what's off-frame", desc: "Inappropriate behavior? Block and report. The team moderates actively." },
  ],
};

export default function Safety() {
  const navigate = useNavigate();
  const lang = localStorage.getItem("swap_lang") || "fr";
  const rules = RULES[lang] || RULES.fr;

  return (
    <div className="max-w-lg mx-auto px-4 pt-4 pb-8 min-h-screen" style={{ backgroundColor: "#FFF4E8" }}>
      <div className="flex items-center justify-between mb-6">
        <button onClick={() => navigate(-1)} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition">
          <ArrowLeft className="h-4 w-4" />{lang === "en" ? "Back" : "Retour"}
        </button>
        <LanguageToggle />
      </div>

      <div className="flex items-center gap-2 mb-2">
        <Shield className="h-5 w-5" style={{ color: "#FF6B4A" }} />
        <h1 className="text-xl font-extrabold">{lang === "en" ? "Community Rules" : "Règles de la communauté"}</h1>
      </div>
      <p className="text-sm text-muted-foreground mb-8">
        {lang === "en" ? "A framework for useful, respectful exchanges." : "Un cadre pour des échanges utiles et respectueux."}
      </p>

      <div className="space-y-3">
        {rules.map((r, i) => (
          <motion.div key={i} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }}
            className="bg-white rounded-2xl border border-border p-4 flex gap-3">
            <span className="text-lg font-bold shrink-0" style={{ color: "#FF6B4A" }}>{r.icon}</span>
            <div>
              <h3 className="font-bold text-sm text-foreground mb-0.5">{r.title}</h3>
              <p className="text-xs text-muted-foreground leading-relaxed">{r.desc}</p>
            </div>
          </motion.div>
        ))}
      </div>

      <div className="mt-8 p-4 rounded-2xl border-2" style={{ borderColor: "#B9F85A", backgroundColor: "#B9F85A10" }}>
        <p className="text-xs font-bold text-foreground mb-3">{lang === "en" ? "If something is wrong:" : "En cas de problème :"}</p>
        <div className="flex gap-4">
          {[
            { icon: Ban, label: lang === "en" ? "Block" : "Bloquer" },
            { icon: Flag, label: lang === "en" ? "Report" : "Signaler" },
            { icon: Trash2, label: lang === "en" ? "Delete" : "Supprimer" },
          ].map(({ icon: Icon, label }) => (
            <div key={label} className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <Icon className="h-3.5 w-3.5" />{label}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}